// Localização: frontend/src/components/Financeiro/GraficoFluxoCaixaFuturo.jsx
// Gráfico de Fluxo de Caixa Futuro

import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import { Calendar } from 'lucide-react';

/**
 * @component GraficoFluxoCaixaFuturo
 * @description Gráfico mostrando previsão de recebimentos, pagamentos e saldo projetado
 * @param {Object} previsao - Objeto com previsao_recebimentos e previsao_pagamentos
 * @param {number} saldoAtualContas - Saldo total atual das contas bancárias (do resumo)
 * @param {boolean} isLoading - Estado de carregamento
 * @param {string} error - Mensagem de erro
 * @param {Function} onPeriodChange - Callback quando o período muda
 * @param {number} periodo - Período selecionado em dias (90, 180, 365, 730)
 */
const GraficoFluxoCaixaFuturo = ({ 
    previsao = null,
    saldoAtualContas = 0,
    isLoading = false, 
    error = null,
    onPeriodChange = null,
    periodo = 365
}) => {
    const [viewType, setViewType] = useState('area'); // 'area' ou 'line'
    const [isMobile, setIsMobile] = useState(false);

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Combinar dados de recebimentos e pagamentos em uma estrutura única por data
    const chartData = useMemo(() => {
        if (!previsao) {
            return [];
        }

        // Verificar diferentes possíveis estruturas (pode vir com ou sem prefixo)
        const recebimentos = Array.isArray(previsao.previsao_recebimentos) 
            ? previsao.previsao_recebimentos 
            : Array.isArray(previsao.recebimentos) 
                ? previsao.recebimentos 
                : [];
                
        const pagamentos = Array.isArray(previsao.previsao_pagamentos) 
            ? previsao.previsao_pagamentos 
            : Array.isArray(previsao.pagamentos) 
                ? previsao.pagamentos 
                : [];


        // Se não houver nenhum dado, retornar array vazio
        if (recebimentos.length === 0 && pagamentos.length === 0) {
            return [];
        }

        // Criar um mapa de datas
        const dataMap = new Map();

        // Adicionar recebimentos
        recebimentos.forEach(item => {
            // Normalizar data - pode vir como string (YYYY-MM-DD) ou Date object
            let data = item.data;
            if (data instanceof Date) {
                data = data.toISOString().split('T')[0];
            } else if (typeof data === 'string' && data.includes('T')) {
                // Se for ISO string com hora, extrair apenas a data
                data = data.split('T')[0];
            }
            
            if (!data) {
                console.warn('Item de recebimento sem data:', item);
                return;
            }

            if (!dataMap.has(data)) {
                dataMap.set(data, {
                    data,
                    recebimentos: 0,
                    pagamentos: 0,
                    saldo: 0,
                    totalRecebimentos: 0,
                    totalPagamentos: 0
                });
            }
            const entry = dataMap.get(data);
            const valor = parseFloat(item.valor) || 0;
            entry.recebimentos += valor;
            entry.totalRecebimentos += valor;
        });

        // Adicionar pagamentos
        pagamentos.forEach(item => {
            // Normalizar data - pode vir como string (YYYY-MM-DD) ou Date object
            let data = item.data;
            if (data instanceof Date) {
                data = data.toISOString().split('T')[0];
            } else if (typeof data === 'string' && data.includes('T')) {
                // Se for ISO string com hora, extrair apenas a data
                data = data.split('T')[0];
            }
            
            if (!data) {
                console.warn('Item de pagamento sem data:', item);
                return;
            }

            if (!dataMap.has(data)) {
                dataMap.set(data, {
                    data,
                    recebimentos: 0,
                    pagamentos: 0,
                    saldo: 0,
                    totalRecebimentos: 0,
                    totalPagamentos: 0
                });
            }
            const entry = dataMap.get(data);
            const valor = parseFloat(item.valor) || 0;
            entry.pagamentos += valor;
            entry.totalPagamentos += valor;
        });

        // Converter mapa para array e ordenar por data
        const dataArray = Array.from(dataMap.values()).sort((a, b) => {
            return new Date(a.data) - new Date(b.data);
        });

        // Função auxiliar para formatar data sem problemas de timezone
        const formatarDataSemTimezone = (dataString) => {
            // dataString está no formato YYYY-MM-DD
            const partes = dataString.split('-');
            if (partes.length === 3) {
                const [ano, mes, dia] = partes;
                // Formatar diretamente como DD/MM, evitando conversão de timezone
                return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}`;
            }
            // Fallback: tentar usar Date (menos confiável)
            return new Date(dataString + 'T12:00:00').toLocaleDateString('pt-BR', { 
                day: '2-digit', 
                month: '2-digit' 
            });
        };

        // Calcular saldo acumulado começando do saldo atual das contas bancárias
        // Prioridade: saldoAtualContas (do resumo - já inclui transações efetivadas) > saldo_atual da previsão
        // Verifica se saldoAtualContas foi passado (mesmo que seja 0, é um valor válido)
        const saldoAtual = saldoAtualContas !== undefined && saldoAtualContas !== null
            ? parseFloat(saldoAtualContas)
            : parseFloat(previsao?.saldo_atual || previsao?.saldoTotal || 0);
        let saldoAcumulado = saldoAtual;
        
        // Adicionar ponto inicial (hoje) com saldo atual
        // Usar data atual no formato YYYY-MM-DD sem conversão de timezone
        const hojeObj = new Date();
        const hoje = `${hojeObj.getFullYear()}-${String(hojeObj.getMonth() + 1).padStart(2, '0')}-${String(hojeObj.getDate()).padStart(2, '0')}`;
        const resultado = [{
            data: formatarDataSemTimezone(hoje),
            dataOriginal: hoje,
            recebimentos: 0,
            pagamentos: 0,
            saldo: saldoAtual
        }];

        // Adicionar dados das previsões
        dataArray.forEach(entry => {
            saldoAcumulado += entry.recebimentos - entry.pagamentos;
            resultado.push({
                data: formatarDataSemTimezone(entry.data),
                dataOriginal: entry.data,
                recebimentos: entry.recebimentos,
                pagamentos: entry.pagamentos,
                saldo: saldoAcumulado
            });
        });

        return resultado;
    }, [previsao]);

    if (isLoading) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <h3 className="text-lg font-semibold mb-4">Fluxo de Caixa Futuro</h3>
                    <div className="py-6">
                        <LoadingIndicator message="Carregando previsões..." />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <h3 className="text-lg font-semibold mb-4">Fluxo de Caixa Futuro</h3>
                    <div className="py-4">
                        <Alert type="error" message={error} />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!previsao || chartData.length === 0) {
        // Mostrar informações de debug se não houver dados
        const recebimentos = previsao?.previsao_recebimentos || previsao?.recebimentos || [];
        const pagamentos = previsao?.previsao_pagamentos || previsao?.pagamentos || [];
        
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <h3 className="text-lg font-semibold mb-4">Fluxo de Caixa Futuro</h3>
                    <div className="h-96 flex flex-col items-center justify-center py-6">
                        <p className="text-gray-500 mb-2">Sem previsões para exibir.</p>
                        {previsao && (
                            <p className="text-xs text-gray-400">
                                Recebimentos: {recebimentos.length || 0} | 
                                Pagamentos: {pagamentos.length || 0}
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Função auxiliar para formatar data completa no tooltip
    const formatarDataCompleta = (dataString) => {
        // dataString está no formato YYYY-MM-DD
        const partes = dataString.split('-');
        if (partes.length === 3) {
            const [ano, mes, dia] = partes;
            // Formatar diretamente como DD/MM/YYYY, evitando conversão de timezone
            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
        }
        // Fallback
        return new Date(dataString + 'T12:00:00').toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
    };

    // Tooltip customizado
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;
            const dataCompleta = data?.dataOriginal 
                ? formatarDataCompleta(data.dataOriginal)
                : label;
            
            return (
                <div className="bg-white p-3 border border-gray-300 shadow-md rounded-md text-sm">
                    <p className="font-semibold text-gray-700 mb-2">{dataCompleta}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="mb-1">
                            {`${entry.name}: ${new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                            }).format(entry.value)}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
            <CardContent className="pt-6 pb-0 px-6 h-full flex flex-col" style={{ paddingBottom: '0px' }}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                    <div>
                        <h3 className="text-lg font-semibold">Fluxo de Caixa Futuro</h3>
                        <p className="text-xs text-gray-500 mt-1">
                            Previsão baseada em contas pendentes
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Seletor de Período */}
                        {onPeriodChange && (
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-emerald-600" />
                                <span className="text-xs font-medium text-gray-700">Período:</span>
                                <div className="flex gap-1.5 flex-wrap">
                                    {[90, 180, 365, 730].map(dias => (
                                        <button
                                            key={dias}
                                            onClick={() => onPeriodChange(dias)}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md ${
                                                periodo === dias
                                                    ? 'bg-emerald-100 text-emerald-600 border-emerald-300 border-[0.5px] shadow-md font-semibold'
                                                    : 'bg-emerald-50 text-emerald-600 border-transparent hover:border-emerald-300 hover:bg-emerald-100'
                                            }`}
                                        >
                                            {dias === 365 ? '1 ano' : dias === 730 ? '2 anos' : `${dias}d`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Seletor de Tipo de Visualização */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => setViewType('area')}
                                className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                                    viewType === 'area'
                                        ? 'bg-emerald-600 text-white shadow-md'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Área
                            </button>
                            <button
                                onClick={() => setViewType('line')}
                                className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                                    viewType === 'line'
                                        ? 'bg-emerald-600 text-white shadow-md'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Linha
                            </button>
                        </div>
                    </div>
                </div>
                <div className="outline-none flex-1" style={{ outline: 'none', marginBottom: '8px', paddingBottom: '0px', marginTop: '0px', overflow: 'visible' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {viewType === 'area' ? (
                            <AreaChart data={chartData} margin={{ top: 5, right: isMobile ? 10 : 30, left: isMobile ? 45 : 20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRecebimentos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorPagamentos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorSaldoProjetado" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis 
                                    dataKey="data" 
                                    stroke="#555"
                                    style={{ fontSize: '12px' }}
                                    tick={{ fill: '#555' }}
                                    axisLine={{ stroke: '#555', strokeWidth: 1 }}
                                    tickLine={{ stroke: '#555' }}
                                    height={30}
                                />
                                <YAxis 
                                    stroke="#555"
                                    width={isMobile ? 50 : undefined}
                                    tickFormatter={(value) => 
                                        new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            notation: 'compact'
                                        }).format(value)
                                    }
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Area 
                                    type="monotone" 
                                    dataKey="recebimentos" 
                                    stroke="#10b981" 
                                    fillOpacity={1} 
                                    fill="url(#colorRecebimentos)"
                                    name="Recebimentos Previstos"
                                    strokeWidth={2}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="pagamentos" 
                                    stroke="#ef4444" 
                                    fillOpacity={1} 
                                    fill="url(#colorPagamentos)"
                                    name="Pagamentos Previstos"
                                    strokeWidth={2}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="saldo" 
                                    stroke="#3b82f6" 
                                    fillOpacity={1} 
                                    fill="url(#colorSaldoProjetado)"
                                    name="Saldo Projetado"
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        ) : (
                            <LineChart data={chartData} margin={{ top: 5, right: isMobile ? 10 : 30, left: isMobile ? 45 : 20, bottom: -25 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis 
                                    dataKey="data" 
                                    stroke="#555"
                                    style={{ fontSize: '12px' }}
                                    tick={{ fill: '#555' }}
                                    axisLine={{ stroke: '#555', strokeWidth: 1 }}
                                    tickLine={{ stroke: '#555' }}
                                    height={30}
                                />
                                <YAxis 
                                    stroke="#555"
                                    width={isMobile ? 50 : undefined}
                                    tickFormatter={(value) => 
                                        new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            notation: 'compact'
                                        }).format(value)
                                    }
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Line 
                                    type="monotone" 
                                    dataKey="recebimentos" 
                                    stroke="#10b981" 
                                    strokeWidth={3}
                                    name="Recebimentos Previstos"
                                    dot={{ r: 5 }}
                                    activeDot={{ r: 7 }}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="pagamentos" 
                                    stroke="#ef4444" 
                                    strokeWidth={3}
                                    name="Pagamentos Previstos"
                                    dot={{ r: 5 }}
                                    activeDot={{ r: 7 }}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="saldo" 
                                    stroke="#3b82f6" 
                                    strokeWidth={3}
                                    name="Saldo Projetado"
                                    dot={{ r: 5 }}
                                    activeDot={{ r: 7 }}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                    {/* Legend Customizado fora do gráfico */}
                    <div className="flex items-center justify-center gap-6 mt-2 pt-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                            <span className="text-xs text-gray-600">Recebimentos Previstos</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <span className="text-xs text-gray-600">Pagamentos Previstos</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                            <span className="text-xs text-gray-600">Saldo Projetado</span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default GraficoFluxoCaixaFuturo;

